import { useEffect, useMemo, useRef, useState } from "react";
import ReferralQrView from "./ReferralQrView";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Copy, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";
import DateRangePicker from "@/app/components/common/DateRangePicker";
import { Button } from "@/components/ui/button";
import { coin, footerBg3Style } from "@/assets";
import { useSession } from "@/contexts/AuthContext";
import {
  copyToClipboard,
  hapticSelection,
  openExternalUrl,
} from "@/lib/capacitor";
import { routes } from "@/lib/constants";
import { inter, philosopher } from "@/lib/fonts";
import { cn } from "@/lib/utils";
import { useRtkInfiniteList } from "@/lib/hooks/useRtkInfiniteList";
import { defaultPaginationOptions, endpoints, IUser } from "muminsmart-utils";

function formatReferralDate(date: Date) {
  return format(date, "dd MMMM yyyy • EEE • h:mmaaa");
}

function buildReferralLink(referralCode: string) {
  if (typeof window === "undefined") {
    return `${routes.auth.signup}?referralCode=${encodeURIComponent(referralCode)}`;
  }

  return `${window.location.origin}${routes.auth.signup}?referralCode=${encodeURIComponent(referralCode)}`;
}

function buildReferralShareMessage(referralLink: string, referralCode: string) {
  return `Join Mumin's Mart using my referral link:\n${referralLink}\n\nReferral code: ${referralCode}`;
}

type ReferralView = "list" | "qr";

const MyReferrals = () => {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const referralCode = session?.user?.referralCode?.trim() ?? "";
  const [view, setView] = useState<ReferralView>("list");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const baseParams = useMemo(() => {
    const params: Record<string, unknown> = {
      ...defaultPaginationOptions,
      sortBy: "createdAt",
      sortOrder: "DESC",
    };

    if (dateRange?.from) {
      params.startDate = dateRange.from.toISOString().split("T")[0];
      params.endDate = (dateRange.to ?? dateRange.from)
        .toISOString()
        .split("T")[0];
    }

    return params;
  }, [dateRange]);

  const {
    items: referrals,
    page,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    isLoading,
    loadPage,
    totalItems,
  } = useRtkInfiniteList<Pick<IUser, "id" | "fullName" | "createdAt">>({
    endpoint: endpoints.referral.me,
    baseParams,
    errorMessage: "Failed to fetch referrals",
  });

  useEffect(() => {
    void loadPage(1, true);
  }, [loadPage, baseParams]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoading) {
          void loadPage(page + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading, loadPage, page]);

  const handleCopyReferralCode = async () => {
    if (!referralCode) {
      toast.error("Referral code not available");
      return;
    }

    const copied = await copyToClipboard(referralCode);

    if (copied) {
      toast.success("Referral code copied");
      return;
    }

    toast.error("Unable to copy referral code");
  };

  const handleGetQrCodes = () => {
    if (!referralCode) {
      toast.error("Referral code not available");
      return;
    }

    setView("qr");
  };

  const handleReferNow = async () => {
    if (!referralCode) {
      toast.error("Referral code not available");
      return;
    }

    const referralLink = buildReferralLink(referralCode);
    const message = buildReferralShareMessage(referralLink, referralCode);
    const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

    await openExternalUrl(waUrl);
  };

  const handleBack = () => {
    void hapticSelection();
    navigate(-1);
  };

  if (view === "qr") {
    return <ReferralQrView onBack={() => setView("list")} />;
  }

  return (
    <>
      <ButtonsHeader heading="My Referrals" showButtons={false} />

      <div className="space-y-5 px-4">
        <div className="overflow-hidden rounded-[28px] border border-[#ECE5DC] bg-white shadow-sm">
          <div className="flex items-center gap-4 px-5 py-5">
            <img
              src={coin}
              alt="Referral rewards"
              className="size-20 object-contain"
            />
            <div>
              <p
                className={`text-5xl font-bold leading-none text-[#F2994A] ${philosopher.className}`}
              >
                {totalItems ?? 0}
              </p>
              <p className={`mt-1 text-sm text-[#8A8A8A] ${inter.className}`}>
                Totals Referred
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGetQrCodes}
            className={`flex w-full items-center justify-center gap-2 bg-[#F2994A] px-4 py-3.5 text-sm font-semibold text-white ${inter.className}`}
          >
            <Sparkles className="size-4 opacity-90" />
            Get Refer Link QR Codes
            <Sparkles className="size-4 opacity-90" />
          </button>
        </div>

        <div className="space-y-3 text-center">
          <p
            className={`text-sm font-semibold text-[#8A8A8A] ${inter.className}`}
          >
            Your Referral Code
          </p>
          <div className="flex items-center justify-between rounded-full border border-dashed border-[#D9D9D9] bg-white px-5 py-3.5">
            <span
              className={`text-lg font-bold tracking-[0.2em] text-[#1A1A1A] ${inter.className}`}
            >
              {referralCode || "—"}
            </span>
            <button
              type="button"
              aria-label="Copy referral code"
              disabled={!referralCode}
              onClick={() => {
                void handleCopyReferralCode();
              }}
              className="rounded-full p-1 text-[#6F6F6F] transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Copy className="size-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <div
        className="mt-5 min-h-[calc(100vh-24rem)] rounded-t-4xl bg-white px-4 pb-20 pt-6"
        style={footerBg3Style}
      >
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <DateRangePicker label="" value={dateRange} onChange={setDateRange} />
        </div>

        <div className="space-y-3">
          {isInitialLoading ? (
            <p
              className={`py-16 text-center text-sm text-gray-400 ${inter.className}`}
            >
              Loading referrals...
            </p>
          ) : referrals.length === 0 ? (
            <p
              className={`py-16 text-center text-sm text-gray-400 ${inter.className}`}
            >
              No referrals found for the selected filters.
            </p>
          ) : (
            referrals.map((referral) => (
              <div
                key={referral.id}
                className="flex items-center justify-between rounded-3xl border border-[#EFEFEF] bg-white px-4 py-4 shadow-[0_1px_8px_rgba(0,0,0,0.04)]"
              >
                <div className="min-w-0 space-y-1">
                  <p className={`text-sm ${inter.className}`}>
                    <span className="text-[#A0A0A0]">To </span>
                    <span className="font-semibold capitalize text-[#1A1A1A]">
                      {referral.fullName}
                    </span>
                  </p>
                  <p className={`text-xs text-[#A0A0A0] ${inter.className}`}>
                    {formatReferralDate(new Date(referral.createdAt))}
                  </p>
                </div>
              </div>
            ))
          )}

          {isLoadingMore && !isInitialLoading ? (
            <p
              className={`py-4 text-center text-sm text-gray-400 ${inter.className}`}
            >
              Loading more referrals...
            </p>
          ) : null}
          <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[#F0F0F0] bg-white/95 px-4 py-4 backdrop-blur-sm">
        <div className="mx-auto flex max-w-lg gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleBack}
            className={cn(
              "h-12 flex-1 rounded-full border-0 bg-[#BDBDBD] text-base font-semibold text-white hover:bg-[#A8A8A8]",
              inter.className,
            )}
          >
            Back
          </Button>
          <Button
            type="button"
            onClick={() => {
              void handleReferNow();
            }}
            disabled={!referralCode}
            className={cn(
              "h-12 flex-1 rounded-full bg-secondary text-base font-semibold text-white hover:bg-secondary/90 disabled:opacity-50",
              inter.className,
            )}
          >
            Refer Now
          </Button>
        </div>
      </div>
    </>
  );
};

export default MyReferrals;
